// PreventiApp · Sprint 4 · Gate 4.3 — Utilidades de presentación.
// Convierte datos técnicos a etiquetas de UX sin exponer score crudo.
// Paleta PreventiApp: sobria, institucional, no alarmista.

// Nivel de alerta → etiqueta y color visual
export const NIVELES = {
  verde:    { label: 'Verde',    prioridad: 'Baja',       bg: '#DFF6EC', fg: '#0F6C4B', dot: '#1E9F73' },
  amarillo: { label: 'Amarillo', prioridad: 'Preventiva', bg: '#FFF3D6', fg: '#8A5A00', dot: '#E0A81E' },
  naranjo:  { label: 'Naranjo',  prioridad: 'Alta',       bg: '#FFE4CC', fg: '#8A4200', dot: '#D97706' },
  rojo:     { label: 'Rojo',     prioridad: 'Crítica',    bg: '#FADBD8', fg: '#8A1616', dot: '#B91C1C' },
};

export function nivelInfo(nivel) {
  return NIVELES[nivel] || NIVELES.verde;
}

// Carril → etiqueta corta y color sobrio
export const CARRILES = {
  convivencia_preventiva: { label: 'Convivencia',      bg: '#EEF0FE', fg: '#5346E6' },
  clinico_orientacion:    { label: 'Clínico',          bg: '#E6F1FA', fg: '#1E5F91' },
  crisis:                 { label: 'Crisis',           bg: '#FAE6EF', fg: '#8B1E4A' },
};

export function carrilInfo(carril) {
  return CARRILES[carril] || { label: carril || '—', bg: '#F4F7FE', fg: '#475472' };
}

// Estado → etiqueta amigable
export const ESTADOS = {
  abierto:                    'Abierto',
  pendiente_primera_accion:   'Pendiente primera acción',
  en_seguimiento:             'En seguimiento',
  pendiente_revision:         'Pendiente revisión',
  cerrado:                    'Cerrado',
};

// Motivos de acción-hoy → chip legible (sin lenguaje alarmista)
export const MOTIVOS = {
  evento_centinela:           { label: 'Evento centinela',       bg: '#FADBD8', fg: '#8A1616' },
  caso_rojo_abierto:          { label: 'Caso rojo abierto',      bg: '#FADBD8', fg: '#8A1616' },
  naranjo_sin_primera_accion: { label: 'Sin primera acción',     bg: '#FFE4CC', fg: '#8A4200' },
  caso_vencido:               { label: 'Seguimiento vencido',    bg: '#FFE4CC', fg: '#8A4200' },
  asignado_a_ti:              { label: 'Asignado a ti',          bg: '#EEF0FE', fg: '#5346E6' },
  revision_programada_hoy:    { label: 'Seguimiento hoy',        bg: '#DFF6EC', fg: '#0F6C4B' },
};

export function motivoInfo(motivo) {
  return MOTIVOS[motivo] || { label: motivo, bg: '#F4F7FE', fg: '#475472' };
}

// Motivos de cierre válidos (Sprint 4 §5)
export const MOTIVOS_CIERRE = [
  { code: 'resuelto',                              label: 'Resuelto' },
  { code: 'derivado_interno',                      label: 'Derivado internamente' },
  { code: 'derivado_externo',                      label: 'Derivado a instancia externa' },
  { code: 'cerrado_con_seguimiento_programado',    label: 'Cerrado con seguimiento programado' },
  { code: 'sin_antecedentes',                      label: 'Sin antecedentes suficientes' },
  { code: 'registro_erroneo',                      label: 'Registro erróneo' },
  { code: 'otro',                                  label: 'Otro (requiere detalle)' },
];

// -----------------------------------------------------------
// Anonimización institucional (Sprint 4 · Gate 4.4 §2 y §3)
// -----------------------------------------------------------
// En vistas ejecutivas (dashboard, listas), NO se debe exponer el nombre real
// del estudiante. Se muestra un identificador anonimizado tipo:
//   "Caso D4-001 · 4°M B"    (caso individual con curso)
//   "Registro D2 · 8° Básico A"
//   "Evento centinela EC-003 · 2° Medio C"
// Solo en el DETALLE de un caso (para roles autorizados) se puede mostrar el nombre real.
export function anonId(caso) {
  if (!caso) return '—';
  const dim = caso.dominio_code || 'D?';
  // "-" antes del sufijo corto derivado del uuid
  const short = (caso.id || '').replace(/-/g, '').slice(0, 4).toUpperCase();
  const prefix = caso.is_sentinel_activo || caso.is_sentinel ? 'EC' : dim;
  return `${prefix}-${short}`;
}

export function anonLabel(caso, opts = {}) {
  if (!caso) return '—';
  const id = anonId(caso);
  const curso = caso.curso_nombre || caso.curso || '';
  const grupal = caso.es_grupal ? ' · Caso grupal' : '';
  if (curso) return `Caso ${id} · ${curso}${grupal}`;
  return `Caso ${id}${grupal}`;
}

// Solo el detalle de caso puede mostrar el nombre real. En el dashboard general
// se usa anonLabel(). Esta constante centraliza la política.
export const MOSTRAR_NOMBRE_REAL_EN_DETALLE_SOLO = true;

// Explicación textual del nivel (para modal "¿Por qué este nivel?")
export const EXPLICACIONES_NIVEL = {
  verde: {
    titulo: 'Nivel Verde · Prioridad Baja',
    descripcion: 'Observación aislada o de baja intensidad. No hay evidencia de reiteración ni de eventos centinela.',
    accion: 'Monitorear. Registrar cualquier observación adicional para detectar patrones.',
  },
  amarillo: {
    titulo: 'Nivel Amarillo · Prioridad Preventiva',
    descripcion: 'Se identifican elementos que requieren atención preventiva del equipo de convivencia u orientación.',
    accion: 'Revisar en próxima instancia de convivencia u orientación. Documentar contexto adicional.',
  },
  naranjo: {
    titulo: 'Nivel Naranjo · Prioridad Alta',
    descripcion: 'La situación combina factores de intensidad, reiteración o dominio sensible que requieren acción concreta.',
    accion: 'Asignar responsable y registrar la primera acción en un plazo acotado.',
  },
  rojo: {
    titulo: 'Nivel Rojo · Prioridad Crítica',
    descripcion: 'Corresponde a eventos centinela, patrones de alta afectación o casos que activan protocolos institucionales.',
    accion: 'Revisión inmediata por parte del equipo directivo y de convivencia. Activar el protocolo correspondiente si aplica.',
  },
};

export const DISCLAIMER_NIVEL =
  'Este nivel orienta la priorización preventiva y la revisión humana. No constituye diagnóstico clínico ni sanción automática.';
